/* "C2 · Połączenie w modalu", transcribed from `C2-Modal-Flow.dc.html` and
 * `C2-Modal-Wyslano.dc.html` — the direction the owner approved on 21 September 2026.
 *
 * ── ONE element, two widths ──────────────────────────────────────────────────────────────────
 * The form is a 560px card and the confirmation is a 980px spread, and they are the SAME
 * `Dialog.Popup`: only the width class changes, so `transition: width` runs. Rendering the
 * confirmation as its own popup would remount the element and the widen would never happen, which
 * is the whole gesture the direction is named after.
 *
 * `transform` is deliberately NOT in the transition list. The card is centred with
 * `left: 50%` + `translate(-50%, -50%)`, and a translate by PERCENTAGE resolves against the
 * element's own box — put it in the list and the centring itself interpolates, which throws the
 * card sideways for the length of the tween. Width alone, from a box that is already centred, grows
 * symmetrically, which is what the mock draws.
 *
 * **Only the BOX tweens. The face inside it is at its final width from the first frame**, reading
 * the same `--invite-width` the dialog does — so the two columns are laid out once, and the widen
 * uncovers them behind `overflow: hidden` instead of squeezing them through 420 intermediate
 * layouts. Measured before the fix: both tracks narrower than their content for 480ms, the cards'
 * two-line titles re-wrapping every frame, and a horizontal scrollbar flashing in and out. The
 * face crossfades in over the first 240ms, which is what hides the uncovering edge.
 *
 * The SCROLLPORT therefore belongs to the face rather than to the dialog. A scrollbar on the
 * dialog would eat 15px out of the box the face is already sized to fill and clip the cards'
 * right-hand edge — a clip with no scroll path, which is the one thing fit-or-scroll forbids.
 *
 * ── The sequence is one duration and a pair of percentages per element ───────────────────────
 * The mock scores fourteen elements at `animation-delay`s from 200ms to 2820ms. When this was
 * built, `tokens/base.css`'s single `prefers-reduced-motion` block collapsed `animation-duration`
 * and NOT `animation-delay`, so a delay would have left a still viewer looking at an empty panel for
 * the best part of three seconds. The block zeroes the delay too since 27 September 2026; the score
 * below stays as built, and is correct either way.
 *
 * So every element runs for the WHOLE sequence — `--dur-invite-sequence`, 3120ms, the mock's last
 * frame — and holds its `from` state until its own ramp, then its `to` state to 100%. The mock's
 * numbers are unchanged; only their spelling is. Under the reduced-motion block the whole score
 * collapses to one frame and every element is at its resting, fully drawn state.
 *
 * Every percentage below is `ms ÷ 3120 × 100`, and each keyframe block carries the mock's own two
 * milliseconds beside it so the transcription can be checked without the arithmetic.
 *
 * The ONE exception is the waiting marker's pulse, which loops: a hold folded into an infinite
 * cycle would repeat on every pass and stop being a pulse. It keeps a real delay (2400ms, once the
 * marker is drawn). Under reduced motion the block zeroes that delay and runs one instant
 * iteration, which ends on a fully transparent ring on an element already drawn: nothing moves.
 *
 * ── The accent, four times ───────────────────────────────────────────────────────────────────
 * The line, the dot travelling along it, the plan card's icon chip, and the submit button's shared
 * ember treatment. Nothing else on either face is tinted, and there is no coloured blur anywhere:
 * the ripple that lands on the second monogram is a NEUTRAL `--text` ring, and the pulse under the
 * waiting marker is a neutral spread.
 *
 * Heights, widths and absolute offsets are layout rather than design tokens (see
 * stylelint.config.js); every colour, space, radius, shadow, duration, easing and type value is a
 * token. */

/* `z-index` pairs with `.dialog` below and matches `ConfirmDialog`'s own stacking, so the two can
 * never be layered the wrong way round when the limit panel hands an act back to the roster. */
.backdrop {
  position: fixed;
  inset: var(--space-0);
  z-index: 30;
  background: var(--scrim);
}

/* `--shadow-modal` is a neutral elevation cue. There is no coloured blur anywhere on this card, and
 * that is an absolute rule in this app rather than a preference.
 *
 * No padding and no column here any more: each face owns its own inset, because the confirmation's
 * two columns run edge to edge and the right one paints its own ground. */
.dialog {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 31;
  width: var(--invite-width);

  /* The face is sized to the box's FINAL width from the first frame, so through the tween it is
   * wider than the box. Clipped rather than scrolled: a scrollbar here would flash in and out for
   * the length of the gesture. The face carries the real scrollport — see the header. */
  overflow: hidden;
  transform: translate(-50%, -50%);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-20px);
  box-shadow: var(--shadow-modal);
  transition-duration: var(--dur-invite-widen);
  transition-property: width;
  transition-timing-function: var(--ease);
}

/* The three widths, declared ONCE each and read by both the box and the face inside it. `min()`
 * rather than a flat number so the card never outgrows a narrow window — the confirmation is the
 * one face in this app wide enough for that to be a real case.
 *
 * The custom property changes on the same commit the class does, so the face is at its new width
 * immediately while `width` on the dialog tweens towards it. That asymmetry is the whole point. */
.dialogNarrow {
  --invite-width: min(480px, calc(100vw - 48px));
}

.dialogForm {
  --invite-width: min(560px, calc(100vw - 48px));
}

.dialogWide {
  --invite-width: min(980px, calc(100vw - 48px));
}

/* ── Every face: its final width, and its own scrollport ──────────────────────────────────────
 * `--overlay-max-block` is the same 24px-a-side reserve this dialog always kept, measured in `dvh`.
 * On a laptop whose browser chrome collapses on scroll, `vh` resolves against the taller viewport,
 * so a face sized to fill the window overhangs it by the toolbar's height — with the submit button
 * in the overhang. */
.form,
.limit,
.sent {
  width: var(--invite-width);
  max-height: var(--overlay-max-block);
  overflow: hidden auto;
  overscroll-behavior: contain;
}

/* Over the corner rather than laid out in a header, so the form's heading block and the
 * confirmation's two columns each own their full width. */
.closeCorner {
  position: absolute;
  top: 18px;
  right: 18px;
  z-index: 2;
}

.close {
  display: grid;
  flex: none;
  place-items: center;
  width: 30px;
  height: 30px;
  font-size: var(--fs-exercise);
  line-height: var(--lh-auto);
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-control);
  cursor: pointer;
  transition-duration: var(--dur-fast);
  transition-property: background, color;
  transition-timing-function: var(--ease);
}

.close:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.close:hover {
  color: var(--text);
  background: var(--hover-tint);
}

/* Read by assistive technology, drawn for nobody. The occupant list repeats one verb per row, so
 * each button carries the name as an sr-only TAIL — a name that does not contain its own visible
 * label fails SC 2.5.3, which is what an `aria-label` here would produce. */
.hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
  clip-path: inset(50%);
}

/* The emphasised run inside a sentence — the seat count on both faces, and the address on the
 * confirmation. One class, because it is one treatment; the `<b>` around it comes from the
 * translation itself through `<Trans>`, so the sentence stays one translatable unit. */
.strong {
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* ── THE FORM FACE ────────────────────────────────────────────────────────────────────────── */

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-22px);
  padding: var(--space-34px) var(--space-38px) var(--space-30px);
}

.formHead {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-2);
}

.eyebrow {
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-rail);
  text-transform: uppercase;
  color: var(--text-3);
}

.formHeading {
  margin: var(--space-0);
  font-size: var(--fs-h1);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-plan);
  letter-spacing: var(--ls-display);
  color: var(--text);
}

/* Two columns, because the mock puts „Imię" and „Nazwisko" on one line — and a grid rather than a
 * nested row, so the e-mail field and a form-level refusal simply span both tracks instead of
 * needing a wrapper each. */
.fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-11px) var(--space-10px);
}

.field {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: var(--space-5px);
}

.fieldWide {
  grid-column: 1 / -1;
}

.label {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-day);
  text-transform: uppercase;
  color: var(--text-2);
}

.labelInvalid {
  color: var(--danger-text-strong);
}

/* The box, not the input, carries the frame: the mock draws a mark inside it beside the caret, and
 * a border on the `<input>` would sit inside that frame rather than being it. */
.box {
  display: flex;
  align-items: center;
  height: 46px;
  gap: var(--space-10px);
  padding: var(--space-0) var(--space-13px);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
}

.box:focus-within {
  box-shadow: var(--ring-field);
}

.boxInvalid {
  border-color: var(--danger);
}

.boxGlyph {
  flex: none;
  width: 15px;
  height: 15px;
  color: var(--text-3);
}

.input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-button);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text);
  background: transparent;
  border: none;
  outline: none;
}

.error {
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--danger-text-strong);
}

.foot {
  display: flex;
  align-items: center;
  gap: var(--space-10px);
}

.hint {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.ghost {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 38px;
  padding: var(--space-0) var(--space-15px);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text);
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition-duration: var(--dur-fast);
  transition-property: background;
  transition-timing-function: var(--ease);
}

/* The limit panel's footer holds one control and pushes it to the end. It was an empty `<span>`
 * wearing `.hint` — a spacer with a content class on it, which reads to the next person as copy
 * that went missing. */
.ghostTrailing {
  margin-left: auto;
}

.ghost:hover {
  background: var(--hover-tint);
}

.ghost:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* ── GEOMETRY ONLY ────────────────────────────────────────────────────────────────────────────
 * The TREATMENT — fill, ink, border, focus ring, hover, refused state — is
 * `shared/ui/emberButton.module.css`, which the CTA also wears. Nothing here may restate anything
 * that module declares.
 *
 * The refused state is the reason this matters beyond de-duplication. The copy here dimmed the
 * gradient with `opacity: 0.6`, which is the exact mistake `AssignPlanDialog` recorded measuring:
 * over the ember ramp that composites the label to roughly 1.3:1, so "Wysyłanie…" — the one frame
 * that owes the trainer an explanation — was the one they could not read. The shared module gives a
 * refused primary the neutral chip treatment instead. */
.cta {
  height: 42px;
  padding: var(--space-0) var(--space-20px);
  font-size: var(--fs-dialog-field);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: normal;
  border-radius: var(--radius-sm);
}

/* ── THE SEAT-LIMIT FACE ──────────────────────────────────────────────────────────────────────
 * Unchanged from the ticket it replaces, down to its own narrower box: it is not part of the C2
 * direction, and a refusal that suddenly grew 80px wider would read as a different screen.
 *
 * KANON: "Przy komplecie miejsc panel nie proponuje zakupu, bo web go nie ma. Wymienia zajmujące
 * osoby z akcją zakończenia współpracy — jedyną drogą do zwolnienia miejsca." */
.limit {
  display: flex;
  flex-direction: column;
  gap: var(--space-15px);
  padding: var(--space-20px) var(--space-22px);
}

.head {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
}

.headText {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  gap: var(--space-2);
}

.limitEyebrow {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-head);
  text-transform: uppercase;
  color: var(--danger-text-strong);
}

.title {
  margin: var(--space-0);
  font-size: var(--fs-dialog-title);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  letter-spacing: var(--ls-display);
  color: var(--text);
}

.body {
  font-size: var(--fs-note);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

/* `list-style: none` because this is layout, not prose — `base.css` zeroes the UA margin and the
 * marker column for every list in this app, but the marker itself belongs to the list that uses
 * one. Every other list here already says so. */
.occupants {
  display: flex;
  flex-direction: column;
  max-height: 260px;
  overflow: auto;
  list-style: none;
}

.occupant {
  display: flex;
  align-items: center;
  gap: var(--space-11px);
  padding: var(--space-9px) var(--space-0);
  border-bottom: var(--border-hairline) solid var(--border);
}

.occupantAvatar {
  display: grid;
  flex: none;
  place-items: center;
  width: 28px;
  height: 28px;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  background: var(--bg-sunken);
  border-radius: var(--r-circle);
}

.occupantName {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-exercise);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.occupantStatus {
  flex: none;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-group);
  text-transform: uppercase;
  color: var(--text-2);
}

.occupantPending {
  color: var(--accent-text);
}

.end {
  display: inline-flex;
  flex: none;
  align-items: center;
  height: 26px;
  padding: var(--space-0) var(--space-10px);
  font-size: var(--fs-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--danger-text-strong);
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition-duration: var(--dur-fast);
  transition-property: background;
  transition-timing-function: var(--ease);
}

.end:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.end:hover {
  background: var(--hover-tint);
}

/* ── THE CONFIRMATION ─────────────────────────────────────────────────────────────────────── */

.sent {
  display: grid;
  grid-template-columns: 1fr 1fr;

  /* The mock's 600px is a FLOOR, not a height, and a floor a 620px window cannot afford put both
   * closing buttons below the fold — the face was 600 tall inside a 572px box. Bounded by the same
   * reserve the face is bounded by, so the floor can never exceed the room there is. */
  min-height: min(600px, var(--overlay-max-block));
  animation-name: invite-face;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.sentLeft {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-7);
  padding: var(--space-52px) var(--space-10) var(--space-44px) var(--space-12);
}

.sentRight {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-5);
  padding: var(--space-52px) var(--space-12) var(--space-44px) var(--space-10);
  background: var(--bg-inset);
  border-left: var(--border-hairline) solid var(--border);
}

/* The app's FIRST height media query, and it is deliberate rather than a lapse. The rule
 * `stylelint.config.js` enforces bans `prefers-color-scheme` — one theme switch, one place — and
 * says nothing about height; this branches on the window, not on a preference the theme toggle
 * has to be able to override.
 *
 * What it spends is the block padding and nothing else. At 1280×620 the face has 572px to work in
 * and the mock's 52/44 gutters take 96 of them, which is what put „Wróć do listy" off screen.
 * 1512×862, the owner's MacBook, keeps the mock's gutters exactly. */
@media (height <= 760px) {
  .sentLeft,
  .sentRight {
    padding-block: var(--space-6);
  }
}

/* ── The picture: two discs, a line, a dot, a tick ─────────────────────────────────────────── */

.pair {
  position: relative;
  display: flex;
  align-items: center;
  height: 96px;
  margin-bottom: var(--space-2);
}

.avatar {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 84px;
  height: 84px;
  font-size: var(--fs-invite-monogram);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-display);
  color: var(--text);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-circle);
}

/* SIZE ONLY — the mark's treatment is `shared/ui/brandMark.module.css`, which the element also
 * wears, and nothing here may restate it. Drawn only for a trainer whose account has no name yet. */
.avatarMark {
  width: 40px;
  height: 40px;
}

/* The two discs arrive from their own sides — 200ms → 620ms. */
.avatarYou {
  animation-name: invite-from-left;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.avatarThem {
  background: var(--bg-card);
  animation-name: invite-from-right;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.avatarTag {
  position: absolute;
  bottom: -24px;
  left: 50%;
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--text-3);
  transform: translateX(-50%);
}

/* The ripple that lands when the dot arrives. A BORDER ring in `--text`, never a coloured blur. */
.avatarThem::after {
  content: "";
  position: absolute;
  inset: var(--space-minus-3px);
  border: var(--border-ring) solid var(--text);
  border-radius: var(--r-circle);
  opacity: 0;
  animation-name: invite-ripple;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.avatarCheck {
  position: absolute;
  right: 0;
  bottom: 0;
  display: grid;
  place-items: center;
  width: 26px;
  height: 26px;
  color: var(--good-ink);
  background: var(--good-soft);
  border: var(--border-knob) solid var(--bg-card);
  border-radius: var(--r-circle);
  animation-name: invite-disc;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease-spring);
  animation-fill-mode: both;
}

.avatarCheckMark {
  width: 13px;
  height: 13px;
  stroke-width: 3;
}

/* The tick is DRAWN rather than faded in — the one stroke on this face that reads as a signature.
 * The dash length is a round over-estimate of the path (about 22 units on a 24 box), so the offset
 * hides it completely at rest and the ramp uncovers it exactly once. */
.avatarCheckMark path {
  stroke-dasharray: 26;
  animation-name: invite-draw;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.line {
  position: relative;
  flex: 1;
  height: 2px;
  margin: var(--space-0) var(--space-6px);
}

/* The RESTING ink is declared here and not only in the keyframes: an element whose
 * `animation-name` is dropped — which is what the keyboard escape hatch at the foot of this file
 * does — falls back to its own rule, and a line with no background at all is no line. */
.lineBase {
  position: absolute;
  inset: var(--space-0);
  background: var(--border-2);
  transform-origin: left;
  animation-name: invite-line;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

/* ── The travelling dot is a full-width CARRIER, and the dot itself is its ::before ──────────
 * Animating `left` relaid out the line on every one of the 800ms of travel. A transform does not,
 * but `translateX(100%)` resolves against the ELEMENT's own width — so the element is made as wide
 * as the line it crosses, and the 10px disc is painted at its leading edge. `100% - 12px` is then
 * the mock's own end position, expressed against the right box. */
.lineDot {
  position: absolute;
  top: -4px;
  left: 0;
  width: 100%;
  height: 10px;
  opacity: 0;
  animation-name: invite-dot;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease-in-out);
  animation-fill-mode: both;
}

.lineDot::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 10px;
  height: 10px;
  background: var(--primary-fill);
  border-radius: var(--r-circle);
}

/* ── The heading, the sentence, the timeline ──────────────────────────────────────────────── */

.headingRise {
  animation-name: invite-heading;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.sentHeading {
  margin: var(--space-0);
  font-size: var(--fs-invite-sent);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-heading);
  letter-spacing: var(--ls-home-title);
  color: var(--text);
}

/* Focus lands here when the face replaces the form, so the confirmation is announced rather than
 * dropped on the floor.
 *
 * `:focus-visible` rather than `:focus`, and the ring is NOT suppressed. A trainer who pressed
 * Enter on „Wyślij zaproszenie" is navigating by keyboard, the browser carries that modality into
 * the programmatic focus that follows, and they get a ring on the thing that now holds it — a
 * silent move is how a keyboard user loses their place. One who clicked gets no ring, because a
 * heading wearing one after every submit is noise. */
.sentHeading:focus {
  outline: none;
}

.sentHeading:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.sentBody {
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-lede);
  color: var(--text-2);
  animation-name: invite-body;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

/* The host's warning on the confirmation rises with the body it qualifies. */
.sentNote {
  animation-name: invite-body;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.timeline {
  display: flex;
  flex-direction: column;
  list-style: none;
}

.step {
  position: relative;
  display: flex;
  align-items: center;
  height: 40px;
  gap: var(--space-3);
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

/* The connector between two steps, growing downward out of the marker above it. */
.step::before {
  content: "";
  position: absolute;
  top: 31px;
  left: 10px;
  width: 2px;
  height: 10px;
  background: var(--border-2);
  transform-origin: top;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.step:last-child::before {
  display: none;
}

.stepMark {
  display: grid;
  flex: none;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: var(--r-circle);
}

.stepMarkDone {
  color: var(--good-ink);
  background: var(--good-soft);
}

.stepCheck {
  width: 12px;
  height: 12px;
  stroke-width: 3;
}

.stepMarkNow {
  background: var(--bg-card);
  border: var(--border-ring) solid var(--text);
  animation-name: invite-ring;
  animation-duration: var(--dur-invite-ring);
  animation-delay: var(--delay-invite-ring);
  animation-timing-function: var(--ease);
  animation-iteration-count: infinite;
  animation-fill-mode: both;
}

.stepDot {
  display: block;
  width: 7px;
  height: 7px;
  background: var(--text);
  border-radius: var(--r-circle);
}

.stepMarkTodo {
  border: var(--border-ring) dashed var(--border-2);
}

.stepLabel {
  font-size: var(--fs-dialog-field);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
}

.stepWhen {
  margin-left: auto;
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-3);
}

/* ── The right column ─────────────────────────────────────────────────────────────────────── */

.prepEyebrow {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-rail);
  text-transform: uppercase;
  color: var(--text-3);
  animation-name: invite-prep-eyebrow;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.prepHeading {
  margin: var(--space-0);
  font-size: var(--fs-stat);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-display);
  letter-spacing: var(--ls-day-title);
  color: var(--text);
  animation-name: invite-prep-heading;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.cards {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.card {
  display: flex;
  align-items: center;
  gap: var(--space-14px);
  padding: var(--space-13px) var(--space-14px);
  color: var(--text);
  text-decoration: none;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
  transition-duration: var(--dur-fast);
  transition-property: background;
  transition-timing-function: var(--ease);
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.card:hover {
  background: var(--bg-chip);
}

.card:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.cardIcon {
  display: grid;
  flex: none;
  place-items: center;
  width: 34px;
  height: 34px;
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--radius-sm);
}

.cardGlyph {
  width: 18px;
  height: 18px;
}

.cardText {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: var(--space-1px);
}

.cardTitle {
  font-size: var(--fs-dialog-field);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-heading);
}

.cardBody {
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-note);
  color: var(--text-2);
}

.cardGo {
  flex: none;
  width: 16px;
  height: 16px;
  margin-left: auto;
  color: var(--text-3);
}

.cardPeek {
  flex: none;
  margin-left: auto;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  white-space: nowrap;
  color: var(--text-2);
}

/* The ONE accent on the card set. It marks the act a trainer most often takes next, and the two
 * neutral chips beside it are what keep it legible as a choice rather than as decoration. */
.cardHot .cardIcon {
  color: var(--accent-text);
  background: var(--accent-soft);
}

.cardHot .cardGo {
  color: var(--accent-text);
}

/* A statement rather than an act: it reports that the intake already went out, and it leads
 * nowhere. So it takes neither the pointer cursor nor the hover the two links carry — an inert card
 * that lights up under the pointer is a promise it cannot keep. */
.cardDone {
  cursor: default;
}

.cardDone:hover {
  background: var(--bg-card);
}

.cardDone .cardIcon {
  color: var(--good-ink);
  background: var(--good-soft);
}

/* Not a link and not a disabled control: there is nothing behind it to reach on this platform
 * today, and a focusable stop that leads nowhere is a dead affordance. The chip says why in words,
 * so the state is not carried by the dashed edge alone. */
.cardSoon {
  background: transparent;
  border-style: dashed;
  border-color: var(--border-2);
  cursor: default;
}

.cardSoon:hover {
  background: transparent;
}

.cardSoon .cardIcon {
  color: var(--text-3);
  background: var(--bg-chip);
}

.cardSoon .cardTitle {
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.cardSoon .cardBody {
  color: var(--text-3);
}

.cardSoonChip {
  flex: none;
  margin-left: auto;
  padding: var(--space-1) var(--space-2);
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-group);
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--text-3);
  background: var(--bg-chip);
  border-radius: var(--r-pill);
}

.sentFoot {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  margin-top: var(--space-6px);
  animation-name: invite-foot;
  animation-duration: var(--dur-invite-sequence);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.sentSeats {
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-relaxed);
  color: var(--text-3);
}

.sentActions {
  display: flex;
  gap: var(--space-10px);
}

/* The two closing controls share the footer evenly, which is the mock's `flex: 1` on each. The
 * element wears `.ghost` as well and this adds only the share, so nothing is restated. */
.sentGhost {
  flex: 1;
  justify-content: center;
}

/* GEOMETRY ONLY. The treatment — the ink fill, its hover, its focus ring — is
 * `shared/ui/inkButton.module.css`, which the element also wears. */
.sentInk {
  flex: 1;
  justify-content: center;
  height: 38px;
  padding: var(--space-0) var(--space-15px);
  font-size: var(--fs-note);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: normal;
  border-radius: var(--radius-sm);
}

/* ── THE SCORE ────────────────────────────────────────────────────────────────────────────────
 * Every element above carries the same three lines — `--dur-invite-sequence`, `--ease`, `both` —
 * beside its own `animation-name`. They were one shared selector list until Stylelint refused it:
 * every one of those elements already has a rule of its own, so the list was a duplicate selector
 * for each of them and put a bare `.card` after `.card:hover`. Three repeated declarations beat a
 * block that has to be read together with twelve others.
 *
 * What follows is the score itself: the mock's milliseconds, and the percentage each one is at. */

@keyframes invite-from-left {
  0%,
  6.41% {
    opacity: 0;
    transform: translateX(-40px);
  }

  19.87%,
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes invite-from-right {
  0%,
  6.41% {
    opacity: 0;
    transform: translateX(40px);
  }

  19.87%,
  100% {
    opacity: 1;
    transform: none;
  }
}

/* The line grows in the accent — 500ms → 1200ms — and cools to the neutral hairline once the tick
 * has landed — 1500ms → 2200ms. Two of the mock's animations on one element, merged into one
 * timeline, which is what having a single duration makes possible. */
@keyframes invite-line {
  0%,
  16.03% {
    background: var(--accent);
    transform: scaleX(0);
  }

  38.46%,
  48.08% {
    background: var(--accent);
    transform: scaleX(1);
  }

  70.51%,
  100% {
    background: var(--border-2);
    transform: scaleX(1);
  }
}

/* The dot travels the line — 520ms → 1320ms — fading in over the first tenth of its own trip and
 * out over the last, which is what makes it read as arriving rather than as stopping. */
@keyframes invite-dot {
  0%,
  16.67% {
    opacity: 0;
    transform: translateX(0);
  }

  19.23% {
    opacity: 1;
  }

  39.74% {
    opacity: 1;
  }

  42.31%,
  100% {
    opacity: 0;
    transform: translateX(calc(100% - 12px));
  }
}

/* The neutral ring the landing throws off — 1300ms → 2020ms. */
@keyframes invite-ripple {
  0%,
  41.67% {
    opacity: 0.35;
    transform: scale(1);
  }

  64.74%,
  100% {
    opacity: 0;
    transform: scale(1.4);
  }
}

/* The tick's disc springs in — 1340ms → 1660ms — and its stroke is drawn after it — 1480ms →
 * 1800ms. */
@keyframes invite-disc {
  0%,
  42.95% {
    opacity: 0;
    transform: scale(0.6);
  }

  53.21%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes invite-draw {
  0%,
  47.44% {
    stroke-dashoffset: 26;
  }

  57.69%,
  100% {
    stroke-dashoffset: 0;
  }
}

/* „Wysłane!" rises on the frame the line begins to cool — 1500ms → 1980ms — and the sentence
 * follows it — 1640ms → 2000ms. */
@keyframes invite-heading {
  0%,
  48.08% {
    opacity: 0;
    transform: translateY(12px);
  }

  63.46%,
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes invite-body {
  0%,
  52.56% {
    opacity: 0;
    transform: translateY(12px);
  }

  64.1%,
  100% {
    opacity: 1;
    transform: none;
  }
}

/* The three steps, 120ms apart — 1750 / 1870 / 1990ms, each over 320ms — with the connector under
 * each growing 100ms behind it. */
.step:nth-child(1) {
  animation-name: invite-step-1;
}

.step:nth-child(2) {
  animation-name: invite-step-2;
}

.step:nth-child(3) {
  animation-name: invite-step-3;
}

.step:nth-child(1)::before {
  animation-name: invite-link-1;
}

.step:nth-child(2)::before {
  animation-name: invite-link-2;
}

@keyframes invite-step-1 {
  0%,
  56.09% {
    opacity: 0;
    transform: translateY(12px);
  }

  66.35%,
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes invite-step-2 {
  0%,
  59.94% {
    opacity: 0;
    transform: translateY(12px);
  }

  70.19%,
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes invite-step-3 {
  0%,
  63.78% {
    opacity: 0;
    transform: translateY(12px);
  }

  74.04%,
  100% {
    opacity: 1;
    transform: none;
  }
}

@keyframes invite-link-1 {
  0%,
  59.29% {
    transform: scaleY(0);
  }

  65.71%,
  100% {
    transform: scaleY(1);
  }
}

@keyframes invite-link-2 {
  0%,
  63.14% {
    transform: scaleY(0);
  }

  69.55%,
  100% {
    transform: scaleY(1);
  }
}

/* The waiting marker keeps pulsing after the sequence has finished. The only looping animation
 * here, and therefore the only one with a real `animation-delay` — see the header. */
@keyframes invite-ring {
  0% {
    box-shadow: var(--shadow-invite-ring);
  }

  100% {
    box-shadow: var(--shadow-invite-ring-out);
  }
}

/* The right column, after the left has settled — 2000ms and 2080ms. */
@keyframes invite-prep-eyebrow {
  0%,
  64.1% {
    opacity: 0;
  }

  73.72%,
  100% {
    opacity: 1;
  }
}

@keyframes invite-prep-heading {
  0%,
  66.67% {
    opacity: 0;
    transform: translateY(12px);
  }

  78.21%,
  100% {
    opacity: 1;
    transform: none;
  }
}

/* The four cards, 120ms apart — 2200 / 2320 / 2440 / 2560ms, each over 360ms. */
.card:nth-child(1) {
  animation-name: invite-card-1;
}

.card:nth-child(2) {
  animation-name: invite-card-2;
}

.card:nth-child(3) {
  animation-name: invite-card-3;
}

.card:nth-child(4) {
  animation-name: invite-card-4;
}

@keyframes invite-card-1 {
  0%,
  70.51% {
    visibility: hidden;
    opacity: 0;
    transform: translateY(24px) scale(0.96);
  }

  82.05%,
  100% {
    visibility: visible;
    opacity: 1;
    transform: none;
  }
}

@keyframes invite-card-2 {
  0%,
  74.36% {
    visibility: hidden;
    opacity: 0;
    transform: translateY(24px) scale(0.96);
  }

  85.9%,
  100% {
    visibility: visible;
    opacity: 1;
    transform: none;
  }
}

@keyframes invite-card-3 {
  0%,
  78.21% {
    visibility: hidden;
    opacity: 0;
    transform: translateY(24px) scale(0.96);
  }

  89.74%,
  100% {
    visibility: visible;
    opacity: 1;
    transform: none;
  }
}

@keyframes invite-card-4 {
  0%,
  82.05% {
    visibility: hidden;
    opacity: 0;
    transform: translateY(24px) scale(0.96);
  }

  93.59%,
  100% {
    visibility: visible;
    opacity: 1;
    transform: none;
  }
}

/* The footer closes the sequence — 2820ms → 3120ms, which is where `--dur-invite-sequence` comes
 * from. */
@keyframes invite-foot {
  0%,
  90.38% {
    visibility: hidden;
    opacity: 0;
  }

  100% {
    visibility: visible;
    opacity: 1;
  }
}

/* The face crossfades in over the first 240ms while the box is still widening behind it — see the
 * header. It is the one animation here whose subject is the whole panel. */
@keyframes invite-face {
  0% {
    opacity: 0;
  }

  7.69%,
  100% {
    opacity: 1;
  }
}

/* A host's warning over the fields — the unconfirmed address of a public-form lead (public-form
 * R13). Amber, not danger: nothing is refused, the trainer is told before they send. */
.notice {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  margin: var(--space-0);
  padding: var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--warn-soft);
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);
  color: var(--warn-ink);
}

.noticeIcon {
  flex: none;
  width: var(--icon-xs);
  height: var(--icon-xs);
  margin-top: var(--space-1);
}

/* ── THE KEYBOARD ESCAPE HATCH ────────────────────────────────────────────────────────────────
 * One real keyboard contact ends the score: every element drops its `animation-name` and falls to
 * the resting state its own rule declares. Nothing snaps out of position, because every animation
 * above ends where its element already sits.
 *
 * ── Why `:has(:focus-visible)` and not `:focus-within` ──────────────────────────────────────
 * `:focus-within` is true from the first frame here, and not because of anything the trainer did:
 * the dialog moves focus to the heading the moment this face replaces the form, so the whole
 * ceremony would be cancelled for everybody, on every send. `:focus-visible` is the modality
 * question rather than the focus question — it matches the trainer who pressed Enter on „Wyślij
 * zaproszenie" or has since pressed Tab, and not the one who clicked. Keyboard gets the content
 * immediately; pointer gets the mock's sequence.
 *
 * ── Scoped to the POPUP, not the face ─────────────────────────────────────────────────────────
 * After a pointer send, every tabbable element inside the face is `visibility: hidden` until its
 * own stop, so the first Tab lands on the one control that is not: the ✕, which is a SIBLING of
 * the face. A hatch scoped to `.sent` could never see that press; scoped to the dialog it does.
 *
 * It has to be the LAST rule in the file. `:has()` takes the specificity of its argument, so
 * `.dialog:has(:focus-visible) .sent *` is (0,3,0), above `.card:nth-child(1)` and
 * `.step:nth-child(1)`, and source order settles the rest. */
.dialog:has(:focus-visible) .sent,
.dialog:has(:focus-visible) .sent *,
.dialog:has(:focus-visible) .sent *::before,
.dialog:has(:focus-visible) .sent *::after {
  animation-name: none;
}

